/**
 * useNotificationPreferences Hook
 *
 * Wraps getMyProfile query and updateMyNotificationPreferences mutation
 * for managing customer notification preferences.
 *
 * Requirements: US-1 — Notification Preferences
 */

import { useCallback } from 'react';
import { useToast } from '@bloomcode/ui';
import {
  useGetMyProfileQuery,
  useUpdateMyNotificationPreferencesMutation,
  type GetMyProfileQuery,
  type NotificationPreferencesInput,
} from '@bloomcode/graphql';

// Type for notification preferences data
type NotificationPreferences = NonNullable<
  NonNullable<
    NonNullable<GetMyProfileQuery['getMyProfile']>['data']
  >['notificationPreferences']['data']
>;

interface UseNotificationPreferencesResult {
  preferences: NotificationPreferences | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: Error | null;
  updatePreference: (key: keyof NotificationPreferencesInput, value: boolean) => Promise<void>;
  refetch: () => void;
}

/**
 * Hook to manage customer notification preferences.
 * Loads current preferences from getMyProfile and provides
 * auto-save mutation for individual preference toggles.
 */
export function useNotificationPreferences(): UseNotificationPreferencesResult {
  const { toast } = useToast();

  // Fetch current profile with notification preferences
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
    refetch,
  } = useGetMyProfileQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Mutation for updating preferences
  const [updateMutation, { loading: isUpdating }] = useUpdateMyNotificationPreferencesMutation({
    refetchQueries: ['GetMyProfile'],
    onError: (error) => {
      toast({
        title: 'Failed to update preferences',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Extract preferences from profile data
  const preferences = profileData?.getMyProfile?.data?.notificationPreferences?.data ?? null;

  /**
   * Update a single notification preference.
   * Auto-saves on toggle with toast feedback.
   */
  const updatePreference = useCallback(
    async (key: keyof NotificationPreferencesInput, value: boolean) => {
      try {
        const result = await updateMutation({
          variables: {
            input: { [key]: value },
          },
        });

        if (result.data?.updateMyNotificationPreferences?.success) {
          toast({
            title: 'Preferences updated',
            description: 'Your notification preferences have been saved.',
          });
        } else {
          const message =
            result.data?.updateMyNotificationPreferences?.message || 'Failed to update preferences';
          toast({
            title: 'Update failed',
            description: message,
            variant: 'destructive',
          });
        }
      } catch {
        // Error already handled by onError callback
      }
    },
    [updateMutation, toast]
  );

  return {
    preferences,
    isLoading: profileLoading,
    isUpdating,
    error: profileError ?? null,
    updatePreference,
    refetch,
  };
}

export default useNotificationPreferences;
